import { useEffect, useState } from 'preact/hooks';
import { DN_CSS, renderDnDocument, type DnDocData } from '../../shared/delivery/document';
import { useI18n } from '../i18n';
import { ApiError, apiGet, apiSend } from '../lib/api';

// The delivery note document (screen 15, P4.2): the same bilingual renderer the broker converts to
// PDF, shown on screen and printed with the browser. Who confirmed the delivery, or Procurement,
// can file it again if the PDF is missing (D58).

interface DocBody {
  data: DnDocData;
  pdf: string;
  canPublish: boolean;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; body: DocBody };

let adopted = false;
/** The document CSS as a constructed stylesheet: CSSOM is not inline style, so the CSP holds. */
function adoptStyles(): void {
  if (adopted || typeof CSSStyleSheet === 'undefined' || !('adoptedStyleSheets' in document)) {
    return;
  }
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(DN_CSS);
  document.adoptedStyleSheets = [...document.adoptedStyleSheets, sheet];
  adopted = true;
}

export function DnDocumentPage({ dnNo }: { dnNo: string }) {
  const { t } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    adoptStyles();
    let live = true;
    apiGet<DocBody>(`/api/dns/${encodeURIComponent(dnNo)}/document`)
      .then((body) => live && setLoad({ kind: 'ok', body }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [dnNo, reload]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{load.code === 'not_found' ? t('dnd.error.notFound') : t('req.loadError')}</p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }
  const { body } = load;

  async function publish() {
    setBusy(true);
    setNote(null);
    try {
      const out = await apiSend<{ pdf: boolean }>(
        'POST',
        `/api/dns/${encodeURIComponent(dnNo)}/publish`,
        {},
      );
      setNote(
        out.pdf ? { ok: true, text: t('dnd.filedNow') } : { ok: false, text: t('dnd.noPdf') },
      );
      if (out.pdf) setReload((r) => r + 1);
    } catch {
      setNote({ ok: false, text: t('dnd.noPdf') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="stack">
      <div class="pod-toolbar no-print">
        <p class="caption">
          <a href={`#/requests/${body.data.prNo}`} dir="ltr">
            {body.data.prNo}
          </a>{' '}
          / <a href={`#/requests/${body.data.prNo}/delivery`}>{t('dl.deliveries')}</a> /{' '}
          <span dir="ltr">{dnNo}</span>
        </p>
        <div class="actions">
          <button type="button" class="button" onClick={() => window.print()}>
            {t('pod.print')}
          </button>
          {body.canPublish && !body.pdf && (
            <button
              type="button"
              class="button button--primary"
              disabled={busy}
              onClick={() => void publish()}
            >
              {t('dnd.file')}
            </button>
          )}
        </div>
        <p class="caption">{body.pdf ? t('pod.filed', { name: body.pdf }) : t('pod.notFiled')}</p>
        {note && (
          <p class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
            {note.text}
          </p>
        )}
      </div>
      {/* Our own renderer's output: every value in it is escaped (shared/delivery/document.ts). */}
      <div
        class="pod-sheet"
        dir="ltr"
        dangerouslySetInnerHTML={{ __html: renderDnDocument(body.data) }}
      />
    </div>
  );
}
